import type Flickity from "flickity";
import React from "react";

// Permissive constructor type since flickity variants have different types than @types/flickity
type FlickityConstructor = new (el: HTMLElement, options: object) => Flickity;
type FlickityModule =
    | "flickity"
    | "flickity-imagesloaded"
    | "flickity-fullscreen";

interface UseFlickityOptions {
    /** Which flickity package to import (default: "flickity-imagesloaded") */
    module?: FlickityModule;
    /** Flickity configuration options */
    options: object;
    /** Whether to initialize flickity (useful for conditional carousels) */
    enabled?: boolean;
    /** Dependencies that should trigger reinitialization */
    deps?: React.DependencyList;
    /** Called after Flickity initializes. Return a cleanup function to run on destroy. */
    onInit?: (flickity: Flickity) => (() => void) | void;
}

interface UseFlickityResult {
    /** Ref to the Flickity instance (null until initialized) */
    flickityRef: React.MutableRefObject<Flickity | null>;
}

/**
 * Flickity's destroy() calls removeChild on DOM nodes that React may have
 * already removed during reconciliation, causing NotFoundError.
 */
export const safeDestroy = (flickity: { destroy: () => void }): void => {
    try {
        flickity.destroy();
    } catch (err) {
        if (err instanceof DOMException && err.name === "NotFoundError") {
            return;
        }
        throw err;
    }
};

/**
 * Hook to manage Flickity carousel instances with SSR safety.
 * Handles dynamic import, initialization, cleanup, and async safety.
 *
 * @example
 * // Simple usage
 * const carouselRef = React.useRef<HTMLDivElement>(null);
 * const { flickityRef } = useFlickity(carouselRef, {
 *     options: { cellAlign: "left", wrapAround: true },
 *     enabled: items.length > 1,
 *     deps: [items],
 * });
 *
 * @example
 * // With event handlers
 * const [selectedIndex, setSelectedIndex] = React.useState(0);
 * const { flickityRef } = useFlickity(carouselRef, {
 *     options: { cellAlign: "center" },
 *     onInit: (flickity) => {
 *         const onSelect = () => setSelectedIndex(flickity.selectedIndex);
 *         flickity.on("select", onSelect);
 *         return () => flickity.off("select", onSelect);
 *     },
 * });
 */
export const useFlickity = <T extends HTMLElement>(
    elementRef: React.RefObject<T | null>,
    {
        module = "flickity-imagesloaded",
        options,
        enabled = true,
        deps = [],
        onInit,
    }: UseFlickityOptions,
): UseFlickityResult => {
    const flickityRef = React.useRef<Flickity | null>(null);
    const flickityClassRef = React.useRef<FlickityConstructor | null>(null);
    const onInitCleanupRef = React.useRef<(() => void) | null>(null);

    React.useEffect(() => {
        // Skip on SSR - flickity requires window
        if (import.meta.env.SSR) {
            return;
        }

        if (!enabled || !elementRef.current) {
            return;
        }

        let mounted = true;

        const initFlickity = (FlickityClass: FlickityConstructor) => {
            if (!mounted || !elementRef.current) {
                return;
            }

            // Clean up previous onInit callback
            if (onInitCleanupRef.current) {
                onInitCleanupRef.current();
                onInitCleanupRef.current = null;
            }

            // Clean up existing instance
            if (flickityRef.current) {
                safeDestroy(flickityRef.current);
                flickityRef.current = null;
            }

            // Create new instance
            flickityRef.current = new FlickityClass(
                elementRef.current,
                options,
            );

            // Call onInit callback
            if (onInit && flickityRef.current) {
                onInitCleanupRef.current = onInit(flickityRef.current) ?? null;
            }
        };

        if (flickityClassRef.current) {
            initFlickity(flickityClassRef.current);
        } else {
            const loadModule = async () => {
                let mod;
                // Use explicit imports to help bundler with code splitting
                switch (module) {
                    case "flickity":
                        mod = await import("flickity");
                        break;
                    case "flickity-fullscreen":
                        mod = await import("flickity-fullscreen");
                        break;
                    case "flickity-imagesloaded":
                    default:
                        mod = await import("flickity-imagesloaded");
                        break;
                }

                if (!mounted) return;

                flickityClassRef.current =
                    mod.default as unknown as FlickityConstructor;
                initFlickity(flickityClassRef.current);
            };

            loadModule();
        }

        return () => {
            mounted = false;
            if (onInitCleanupRef.current) {
                onInitCleanupRef.current();
                onInitCleanupRef.current = null;
            }
            if (flickityRef.current) {
                safeDestroy(flickityRef.current);
                flickityRef.current = null;
            }
        };
    }, [enabled, module, onInit, ...deps]);

    return { flickityRef };
};
